"use client";

import React, { useCallback, useEffect, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";
import { getHomeData } from "@/data/getHomeData";

export default function TestimonialsCarousel() {
	const t = useTranslations("Common");
	const locale = useLocale() as "en" | "ro";
	const { testimonyItems } = getHomeData(locale);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [emblaRef, emblaApi] = useEmblaCarousel(
		{ loop: true, align: "start", skipSnaps: false },
		[Autoplay({ delay: 5000, stopOnInteraction: false })],
	);

	const scrollPrev = useCallback(() => {
		emblaApi?.scrollPrev();
	}, [emblaApi]);
	const scrollNext = useCallback(() => {
		emblaApi?.scrollNext();
	}, [emblaApi]);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setSelectedIndex(emblaApi.selectedScrollSnap());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		onSelect();
		emblaApi.on("select", onSelect);
		return () => {
			emblaApi.off("select", onSelect);
		};
	}, [emblaApi, onSelect]);

	return (
		<div className="w-full">
			<div className="overflow-hidden p-4" ref={emblaRef}>
				<div className="flex -ml-4">
					{testimonyItems.map((item) => (
						<div
							key={item.id}
							className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.33%] min-w-0 pl-4"
						>
							<div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 h-full flex flex-col min-h-[380px] testimonials-card">
								<div className="flex items-center justify-center mb-4 shrink-0">
									<div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border-2 border-gray-100 dark:border-gray-700 shrink-0">
										<Image
											src={item.avatar}
											alt={item.name}
											fill
											className="object-cover"
											sizes="96px"
										/>
									</div>
								</div>
								<div className="text-center mb-3 shrink-0">
									<h4 className="text-[1.8rem] font-bold text-primary leading-tight">
										{item.name}
									</h4>
									<div className="text-[1.6rem] text-gray-500 dark:text-gray-400 mt-1">
										<span className="font-medium">{item.job}</span>
										<span className="mx-1">{t("at")}</span>
										<a
											href={item.company_url}
											target="_blank"
											rel="noopener noreferrer"
											className="text-primary hover:underline"
										>
											{item.company_name}
										</a>
									</div>
								</div>
								<div className="flex-grow min-h-[7rem] flex flex-col">
									<p className="text-[1.6rem] text-gray-600 dark:text-gray-300 italic flex-grow leading-relaxed">
										&quot;{item.paragraph}&quot;
									</p>
								</div>
								<div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 text-[1.4rem] text-gray-400 text-center shrink-0">
									{(item.worked_with || item.past_company_name) && (
										<span>
											(
											{item.worked_with &&
												t("workedWith", { name: item.worked_with })}
											{item.worked_with && item.past_company_name && " "}
											{item.past_company_name &&
												t("workedAt", { company: item.past_company_name })}
											)
										</span>
									)}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Carousel navigation */}
			<div className="flex items-center justify-center gap-4 mt-6">
				<button
					type="button"
					onClick={scrollPrev}
					className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-primary hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
					aria-label={t("prevTestimonial")}
				>
					<svg
						className="w-5 h-5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15 19l-7-7 7-7"
						/>
					</svg>
				</button>
				<div className="flex gap-2">
					{testimonyItems.map((_, index) => (
						<button
							key={index}
							type="button"
							onClick={() => emblaApi?.scrollTo(index)}
							className={`w-2.5 h-2.5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
								index === selectedIndex
									? "bg-primary"
									: "bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500"
							}`}
							aria-label={t("goToTestimonial", { index: index + 1 })}
							aria-current={index === selectedIndex ? "true" : undefined}
						/>
					))}
				</div>
				<button
					type="button"
					onClick={scrollNext}
					className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-primary hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
					aria-label={t("nextTestimonial")}
				>
					<svg
						className="w-5 h-5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M9 5l7 7-7 7"
						/>
					</svg>
				</button>
			</div>
		</div>
	);
}
